import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  Clock,
  CheckCircle2,
  Send,
  Award,
  Calendar,
  Users,
  DollarSign,
  Eye,
  Edit,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-400',
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  closed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  awarded: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

export const RFQManagement = () => {const { t } = useTranslation("procurement");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRfqs = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/procurement/rfqs`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch RFQs');
      const data = await response.json();
      setRfqs(data);
    } catch (err) {
      toast.error('Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRfqs();
  }, [activeTab]);

  const filteredRfqs = rfqs.filter((rfq) => {
    if (!searchTerm) return true;
    return rfq.rfq_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.title?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: rfqs.length,
    open: rfqs.filter((r) => r.status === 'open').length,
    closed: rfqs.filter((r) => r.status === 'closed').length,
    awarded: rfqs.filter((r) => r.status === 'awarded').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="procurement-rfq-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("rfqManagement", "RFQ Management")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("createAndManage", "Create and manage requests for quotation")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRfqs} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-orange-600 hover:bg-orange-700">
            <Plus className="h-4 w-4" />{t("newRfq", "New RFQ")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalRfqs", "Total RFQs")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Send className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("open", "Open")}</p>
                <p className="text-2xl font-bold">{stats.open}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("closed", "Closed")}</p>
                <p className="text-2xl font-bold">{stats.closed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Award className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("awarded", "Awarded")}</p>
                <p className="text-2xl font-bold">{stats.awarded}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="draft">{t("draft", "Draft")}</TabsTrigger>
                <TabsTrigger value="open">{t("open", "Open")}</TabsTrigger>
                <TabsTrigger value="closed">{t("closed", "Closed")}</TabsTrigger>
                <TabsTrigger value="awarded">{t("awarded", "Awarded")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search RFQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("rfqNumber", "RFQ Number")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("title", "Title")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendors", "Vendors")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("responses", "Responses")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("estValue", "Est. Value")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRfqs.map((rfq) =>
                <tr key={rfq.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4">
                      <span className="font-mono text-sm font-medium">{rfq.rfq_number}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-sm">{rfq.title}</p>
                        {rfq.awarded_to &&
                      <p className="text-xs text-emerald-600">{t("awardedTo", "Awarded to:")}{rfq.awarded_to}</p>
                      }
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1.5 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {rfq.due_date}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{rfq.vendors_invited}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge variant="secondary">
                        {rfq.responses_received} / {rfq.vendors_invited}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right font-mono">
                      ${rfq.total_value?.toLocaleString() || 0}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <Badge className={statusColors[rfq.status] || statusColors.draft}>
                        {rfq.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {rfq.status === 'draft' &&
                      <>
                            <Button size="sm" variant="ghost">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="gap-1">
                              <Send className="h-3.5 w-3.5" />{t("send", "Send")}

                        </Button>
                          </>
                      }
                        {rfq.status === 'closed' &&
                      <Button size="sm" variant="outline" className="gap-1">
                            <Award className="h-3.5 w-3.5" />{t("award", "Award")}

                      </Button>
                      }
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredRfqs.length === 0 &&
          <div className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noRfqsFound", "No RFQs found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};